import type { ClientCompany } from '../../types';

import { differenceInSeconds, parseISO } from 'date-fns';

import { Api } from '../../api';
import { Color } from '../../constants';
import { capitalize, entries, values, zeroPad } from '../../util';

export type LabelGroup = Readonly<{
  id: string;
  name: string;
  labels: ReadonlyArray<Api.TicketLabel.BaseView>;
}>;

export type LabelGroups = Readonly<{
  [key: string]: LabelGroup;
}>;

export function isInGroup(group: LabelGroup) {
  return (label: Api.TicketLabel.BaseView): boolean =>
    group.id === 'other' ? label.group == null : label.group === group.id;
}

function isPullRequestEvent(event: { type: string }): boolean {
  return event.type.startsWith('pull_request_');
}

type TimelineCommentItem = Api.Ticket.V_Detail['comments'][number];
export type TimelineEventItem = Api.Ticket.V_Detail['events'][number];
type TimelineItem = TimelineCommentItem | TimelineEventItem;

const GROUPABLE_EVENT_TYPES = new Set([
  'skill_add',
  'skill_remove',
  'label_add',
  'label_remove',
  'skill_approval_change',
  'skill_assignee_change',
  'skill_contributor_add',
  'skill_contributor_remove',
  'skill_status_change',
]);

const GROUP_WINDOW_SECONDS = 10;

function eventContext(event: TimelineEventItem): string {
  const data = event.data as {
    skillType?: string;
    newStatus?: string | null;
    newApproved?: boolean | null;
    newAssigneeId?: string | null;
  } | null;
  switch (event.type) {
    case 'skill_contributor_add':
    case 'skill_contributor_remove':
      return data?.skillType ?? '';
    case 'skill_status_change':
      return data?.newStatus ?? '';
    case 'skill_approval_change':
      return String(data?.newApproved);
    case 'skill_assignee_change':
      return data?.newAssigneeId ?? '';
    default:
      return '';
  }
}

function canMerge(prev: TimelineItem, next: TimelineItem): boolean {
  if (prev['@type'] === 'Comment' || next['@type'] === 'Comment') return false;
  if (prev.type !== next.type || !GROUPABLE_EVENT_TYPES.has(next.type))
    return false;
  const prevUser = prev.user;
  const nextUser = next.user;
  if (!prevUser || !nextUser || prevUser.id !== nextUser.id) return false;
  if (eventContext(prev) !== eventContext(next)) return false;
  const gap = differenceInSeconds(
    parseISO(next.createdAt),
    parseISO(prev.createdAt)
  );
  return Math.abs(gap) <= GROUP_WINDOW_SECONDS;
}

// Expects `items` already sorted ascending by createdAt.
export function groupTimelineItems(
  items: ReadonlyArray<TimelineItem>
): TimelineItem[][] {
  const groups: TimelineItem[][] = [];
  items.forEach((item) => {
    const current = groups[groups.length - 1];
    if (current && canMerge(current[current.length - 1], item)) {
      current.push(item);
    } else {
      groups.push([item]);
    }
  });
  return groups;
}

export function getLabelGroups(
  company: Api.Company.V_AdminList | ClientCompany,
  ticketLabels: Api.TicketLabel.BaseView[]
): LabelGroups {
  const groups: Record<
    string,
    { id: string; name: string; labels: Api.TicketLabel.BaseView[] }
  > = {};
  [...(company.ticketLabels || []), ...ticketLabels].forEach((label) => {
    let key = label.group;
    if (!key) key = 'other';
    if (!groups[key]) {
      groups[key] = {
        // Sorting number is left in `id`, but stripped from `name`
        id: key,
        name: capitalize(key.replace(/^\d+\. /, '').replace(/_/g, ' ')),
        labels: [],
      };
    }
    groups[key].labels.push(label);
  });

  const sortedGroups: Record<string, LabelGroup> = {};
  values(groups)
    .sort((a, b) => a.id.localeCompare(b.id))
    .forEach((group) => {
      sortedGroups[group.id] = group;
    });

  return sortedGroups;
}

export function buildSearchParams(options: {
  page: number;
  status: string;
  sort: string;
  search?: string;
  filters?: Record<string, string[]>;
}): string {
  const { filters, ...rest } = options;
  const base = entries(rest)
    .filter(([, v]) => v !== '' && v != null)
    .map(([k, v]) => `${k}=${v}`);

  if (filters) {
    entries(filters).forEach(([k, ids]) => {
      base.push(`filter_${k}=${ids.map(encodeURIComponent).join(',')}`);
    });
  }

  return base.join('&');
}

export function getSearchParams(existing: string) {
  const urlSearchParams = new URLSearchParams(existing);
  const urlPage = urlSearchParams.get('page');

  const filters: Record<string, string[]> = {};
  urlSearchParams.forEach((value, key) => {
    if (key.startsWith('filter_')) {
      filters[key.slice(7)] = value ? value.split(',') : [];
    }
  });

  return {
    page: urlPage ? parseInt(urlPage, 10) : 1,
    sort:
      urlSearchParams.get('sort') ??
      (urlSearchParams.get('search') ? '' : 'createdAt'),
    status: urlSearchParams.get('status') ?? 'open',
    search: urlSearchParams.get('search') ?? '',
    filters,
  };
}

export function getPriorityFlagColor(priorityName: string): string {
  return priorityName.toLowerCase() === 'low' ? Color.blue : Color.red;
}

export function findLabelGroupByName(
  labelGroups: LabelGroups,
  displayName: string
): LabelGroup | undefined {
  const normalized = displayName.toLowerCase();
  return values(labelGroups).find(
    (group) => group.name.toLowerCase() === normalized
  );
}

export type PullRequest = Api.Ticket.V_Detail['pullRequests'][number];
export type PullRequestReview = PullRequest['latestReviews'][number];

export type PullRequestEventData = Readonly<{
  pullRequestId: string;
  repo: string;
  number: number;
  title: string;
  url: string;
  githubUsername: string;
  reviewState?: string;
}>;

export type PullRequestVisual = Readonly<{
  label: string;
  icon: 'pullRequest' | 'merge' | 'check' | 'comment' | 'x';
  color: string;
}>;

function visual(
  label: string,
  icon: PullRequestVisual['icon'],
  colorValue: string
): PullRequestVisual {
  return { label, icon, color: colorValue };
}

function neutralVisual(label: string): PullRequestVisual {
  return visual(label, 'pullRequest', Color.neutral60);
}

const OPENED = visual('opened', 'pullRequest', Color.green);
const MERGED = visual('merged', 'merge', Color.primaryVibrant);
const APPROVED = visual('approved', 'check', Color.green);
const REJECTED = visual('rejected', 'x', Color.red);
const CLOSED = visual('closed', 'pullRequest', Color.red);

// The backend's own review status enum, confirmed by James on 2026-08-21.
const REVIEW_VISUALS: Readonly<Record<string, PullRequestVisual>> = {
  approved: APPROVED,
  changes_requested: REJECTED,
  commented: visual('commented', 'comment', Color.neutral60),
  dismissed: visual('dismissed', 'x', Color.neutral60),
};

// Keyed on the part of the event type after `pull_request_`. `review` is handled
// separately, from data.reviewState.
const EVENT_VISUALS: Readonly<Record<string, PullRequestVisual>> = {
  open: OPENED,
  merge: MERGED,
};

// A pull request is open, closed or merged; draft is the separate isDraft flag.
const STATE_VISUALS: Readonly<Record<string, PullRequestVisual>> = {
  open: visual('open', 'pullRequest', Color.green),
  closed: CLOSED,
  merged: MERGED,
};

function humanise(value: string): string {
  return value.replace(/_/g, ' ');
}

export function getReviewVisual(review: { state: string }): PullRequestVisual {
  const state = review.state.toLowerCase();
  return REVIEW_VISUALS[state] ?? neutralVisual(humanise(state));
}

export function getPullRequestEventVisual(event: {
  type: string;
  data: object | null;
}): PullRequestVisual | null {
  if (!isPullRequestEvent(event)) return null;
  const action = event.type.toLowerCase().replace('pull_request_', '');
  if (action === 'review') {
    const data = event.data as PullRequestEventData | null;
    if (!data?.reviewState) return neutralVisual('reviewed');
    return getReviewVisual({ state: data.reviewState });
  }
  return EVENT_VISUALS[action] ?? neutralVisual(humanise(action));
}

export function getPullRequestStateVisual(pullRequest: {
  state: string;
  isDraft: boolean;
}): PullRequestVisual {
  if (pullRequest.isDraft) return neutralVisual('draft');
  const state = pullRequest.state.toLowerCase();
  return STATE_VISUALS[state] ?? neutralVisual(humanise(state));
}

/**
 * Minutes as the report shows them: "15m" under an hour, "22h 05m" above,
 * and an em dash when nothing was logged.
 */
export function formatReportMinutes(minutes: number): string {
  if (minutes === 0) return '—';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return hours === 0 ? `${mins}m` : `${hours}h ${zeroPad(mins)}m`;
}
